import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery, useUpdateProjectMutation } from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { numberScheme, stringScheme } from 'src/utils/validation-schemes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React, { useEffect } from 'react';
import moment from 'moment/moment';
import { useLoader } from 'src/provider/LoaderProvider';
import { EditFreshchatSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshchatIntegrationSettings/EditFreshchatSetupSettings/EditFreshchatSetupSettings';
import { EditFreshchatAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshchatIntegrationSettings/EditFreshchatAdvancedSettings/EditFreshchatAdvancedSettings';
import { EditFreshchatApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditFreshchatIntegrationSettings/EditFreshchatApiDetailsSettings/EditFreshchatApiDetailsSettings';

const AccordionIcon = ({ open }) => (
  <svg
    className={clsx('transition-transform', open && 'rotate-180')}
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
  >
    <path
      d="M12.6523 0.651864L6.35244 6.65186L0.652531 0.65187"
      stroke="#7A6BE5"
      strokeWidth="1.79997"
    />
  </svg>
);

export const EditFreshchatIntegrationSettings = ({ disabled = false, integration, onSubmit }) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const loader = useLoader();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const duration = currentProject?.autoclose_time_window_minutes
    ? moment.duration(currentProject?.autoclose_time_window_minutes, 'minutes')
    : 0;
  const autocloseHours = duration ? Math.floor(duration.asHours()) : 0;
  const autocloseMinutes = duration ? duration.minutes() : 0;
  const [updateProject, { isLoading: isLoadingUpdateProject }] = useUpdateProjectMutation();

  useEffect(() => {
    if (isLoadingUpdateProject) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingUpdateProject]);

  const methods = useForm<any>({
    defaultValues: {
      chat_url: integration?.integration_data?.chat_url,
      api_key: integration?.integration_data.api_key,
      webhook_api_token: integration?.integration_data?.webhook_api_token || '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
    },
    resolver: yupResolver(
      object({
        chat_url: stringScheme({ required: false }).label('Chat URL'),
        api_key: stringScheme({ required: false }).label('API Key'),
        webhook_api_token: stringScheme({ required: false }).label('Webhook api token').nullable(),
        autocloseHours: numberScheme({ required: true }),
        autocloseMinutes: numberScheme({ required: true }),
      }),
    ),
  });

  const submit = async (data) => {
    const autocloseMinutes = moment
      .duration(data.autocloseHours, 'hours')
      .add(data.autocloseMinutes, 'minutes')
      .asMinutes();

    const isAutocloseChanged = currentProject?.autoclose_time_window_minutes !== autocloseMinutes;

    if (isAutocloseChanged) {
      await updateProject({
        projectId: currentProject.id,
        name: currentProject?.name,
        rate_limit_project: currentProject?.rate_limit_project,
        rate_limit_project_per: currentProject?.rate_limit_project_per,
        rate_limit_chat: currentProject?.rate_limit_chat,
        rate_limit_chat_per: currentProject?.rate_limit_chat_per,
        rate_limit_max_chats: currentProject?.rate_limit_max_chats,
        rate_limit_max_chats_per: currentProject?.rate_limit_max_chats_per,
        cocbi_project_id: currentProject?.cocbi_project_id,
        use_cocbi_model: currentProject?.use_cocbi_model,
        ai_reply_generation: currentProject?.ai_reply_generation,
        ai_text_lense: currentProject?.ai_text_lense,
        autoclose_time_window_minutes: autocloseMinutes ? autocloseMinutes : null,
      });
    }
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    if (!data.api_key) {
      delete data.api_key;
    }

    if (!data.webhook_api_token) {
      delete data.webhook_api_token;
    }

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="web-widget-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>
                    Set up Conversation Webhook
                  </p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('setup-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshchatSetupSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshchatAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <AccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditFreshchatApiDetailsSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
